<template>
  <transition name="fade">
    <div
      v-if="isOpen">

      <!-- MODAL FOR THE WEIGHT KPIs -->
      <v-dialog persistent v-model="dialogRef" max-width="1200" >
        <template v-slot:activator="{ props }">
          <v-btn color="green-darken-4" @click="dialogRef = true" >
            SET IGNORED WEIGHTS
          </v-btn>
        </template>

        <v-card style="margin-left: 125px">
          <v-card-title><b>Set Ignored Weights</b></v-card-title>
          <v-divider />
          <v-card-item>
            <div>
              <div
                  v-if="ignoreAll === 'not_all_weights'"
                  v-for="(item, index) in selectedPairs"
                  style="margin-bottom: 5px">
                <v-row :class="(index === 0? 'mt-1' : '')">
                  <v-col cols="3" >
                    <v-select
                        v-model="selectedPairs[index]['level']"
                        :items="aggregationLevels"
                        label="Agg. Level"
                        item-title="text"
                        item-value="value"
                        variant="outlined"
                        density="compact"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-select
                        v-model="selectedPairs[index]['dimension']"
                        :items="dimensions"
                        item-title="text"
                        item-value="value"
                        variant="outlined"
                        label="Sector/Dimension"
                        density="compact"
                    />
                  </v-col>
                  <v-col cols="2" >
                    <v-btn class="remove-btn" rounded flat @click="onItemRemove(index)">
                      <i class="fa fa-trash mr-2" /> Remove
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
              <div>
                <v-checkbox
                    id="checkbox-1"
                    label="Ignore all weights"
                    v-model="ignoreAll"
                    name="checkbox-1"
                    value="all_weights"
                    false-value="not_all_weights"
                    @update:modelValue="onIgnoreAll"
                />
              </div>

              <v-btn
                  @click="onAddPair()"
                  rounded color="blue"
                  class="mr-2 mb-1"
                  prepend-icon="fas fa-plus"
                  text="Add Pair"
              />
            </div>
          </v-card-item>
          <v-divider />
          <v-card-actions>
            <v-btn
                color="red-darken-3"
                class="px-5"
                text="close"
                rounded
                @click="dialogRef = false"
            />
            <v-btn
                color="blue-darken-4"
                class="px-5"
                text="Save Preferences"
                prepend-icon="fas fa-check mr-2"
                variant="flat"
                rounded
                @click="onSubmit()"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </transition>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoutes } from "@/composables/useRoutes";
import {router} from "@inertiajs/vue3";
import * as $routes from "@/packs/routes";

const dialogRef = ref(false);

const routes = useRoutes();

const props = defineProps({
  projectId: {
    type: Number,
    required: true,
  },
  ignoredWeightsServer: {
    type: Array,
    required: true,
  },
});

const aggregationLevels = [
  { value: "", text: "Select an aggregation level" },
  { value: "building", text: "Building" },
  { value: "ped", text: "PED" },
  { value: "peb", text: "PEB" },
  { value: "city", text: "City" },
];

const dimensions = [
  { value: "", text: "Select a dimension/sector" },
  // dimensions
  { value: "-", text: "-- Dimensions --", disabled: "true" },
  { value: "economic", text: "Economic" },
  { value: "energy", text: "Energy" },
  { value: "environmental", text: "Environmental" },
  { value: "governance", text: "Governance" },
  { value: "ict", text: "ICT" },
  { value: "mobility", text: "Mobility" },
  { value: "propagation", text: "Propagation" },
  { value: "social", text: "Social" },
  // sectors
  { value: "-", text: "-- Sectors --", disabled: "true" },
  { value: "climate_change_&_mitigation", text: "Climate Change & Mitigation" },
  {
    value: "quality_of_life_&_prosperity",
    text: "Quality of Life & Prosperity",
  },
  { value: "resource_efficiency", text: "Resource Efficiency" },
  {
    value: "smart_&_reliable_infrastructure",
    text: "Smart & Reliable Infrastructure",
  },
];

const successStr = ref("");
const isOpen = ref(true);
const ignoreAll = ref("not_all_weights");

const selectedPairs = computed({
  get() {
    if (props.ignoredWeightsServer.length === 0)
      props.ignoredWeightsServer.push({ dimension: "", level: "" });
    return props.ignoredWeightsServer;
  },
  set(value) {
    return value;
  },
});

onMounted(() => {
  // TODO: this is rather stupid way to check if ignore all is selected
  // should be a column in the db table
  if (selectedPairs.value.length === 49) {
    ignoreAll.value = "all_weights";
  }
});

function onAddPair() {
  selectedPairs.value.push({ dimension: "", level: "" });
}

function onItemRemove(index) {
  if (selectedPairs.value.length > 0) selectedPairs.value.splice(index, 1);
  if (selectedPairs.value.length === 0)
    selectedPairs.value.push({ dimension: "", level: "" });
}

function onIgnoreAll() {
  // add all combinations in selected pairs
  if (ignoreAll.value === "all_weights") {
    selectedPairs.value.splice(0, selectedPairs.value.length);
    for (let i = 0; i < aggregationLevels.length; i++) {
      const level = aggregationLevels[i].value;
      if (level === "") continue;
      for (let j = 0; j < dimensions.length; j++) {
        const dimension = dimensions[j].value;
        if (dimension === "-" || dimension === "") continue;
        selectedPairs.value.push({ dimension, level });
      }
    }
  } else {
    selectedPairs.value.splice(0, selectedPairs.value.length);
  }
}

function onSubmit() {
  router.post($routes.ignore_weights(), {
    project_id: props.projectId,
    pairs: selectedPairs.value,
  });
  selectedPairs.value = [{ dimension: "", level: "" }];
  dialogRef.value = false;
  successStr.value = "Saved preferences";
}
</script>

<style scoped>
.x-btn {
  display: inline-block;
  border-radius: 15%;
  background: transparent;
  position: absolute;
  right: 0;
  margin: 3px;
  border: none;
  width: 30px;
  height: 30px;
  font-size: 20px;
}

.pair-select {
  height: 30px;
  border-radius: 5px;
  background: ghostwhite;
}

.remove-btn {
  line-height: 1;
  padding: 0 10px 0 10px;
  height: 28px;
  margin: 0 0 5px;
  color: white;
  background: #f96362;
  border: none;
  font-size: 14px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
